// =====================================================
// S-I18N-W3 بستهٔ C — یک `t` که هویتش عوض نمی‌شود.
//
// چرا لازم شد: `useTranslation(ns)` هر بار که وضعیتِ آن namespace تغییر کند
// یک تابعِ **تازه** برمی‌گرداند — و چون namespaceها در این پروژه تنبل
// بارگذاری می‌شوند (`src/i18n/index.ts`)، این اتفاق در هر بار mount شدنِ صفحه
// دست‌کم یک‌بار می‌افتد، به‌علاوهٔ هر تعویضِ زبان. اگر `t` داخلِ
// `useCallback`ِ یک واکشی نشسته باشد و آن callback در وابستگی‌هایِ
// `useEffect` بیاید، افکت دوباره شلیک می‌کند و همان درخواستِ شبکه دو (یا
// چهار) بار می‌رود. این رگرسیون واقعی بود و `tests/live/schedule-dialog`
// آن را با «۴ بار به‌جایِ ۲ بار» گرفت.
//
// راهِ حل، نه پاک‌کردنِ `t` از وابستگی‌ها با یک `eslint-disable` (که متنِ
// خطا را برایِ همیشه رویِ زبانِ اولِ mount قفل می‌کرد و lint را هم کور
// می‌کرد)، بلکه یک پوششِ پایدار است: تابعِ برگشتی هرگز عوض نمی‌شود، ولی
// هر بار که صدا زده شود آخرین `t` را صدا می‌زند. پس پیامِ خطایی که *در
// لحظهٔ خطا* ساخته می‌شود همیشه به زبانِ جاری است.
//
// فقط برایِ متنی که داخلِ effect/callback و نه در رندر ساخته می‌شود. متنِ
// معمولیِ رندر باید همان `t`ِ عادی را بگیرد، وگرنه تعویضِ زبان
// رندرِ دوباره‌ای که به آن نیاز دارد را از دست می‌دهد.
// =====================================================

import React from "react";
import type { TFunction } from "i18next";

/** همان `t`، با هویتِ ثابت — امن برایِ وابستگی‌هایِ `useCallback`/`useEffect`. */
export const useStableT = <T extends TFunction>(t: T): T => {
  const ref = React.useRef(t);
  ref.current = t;
  // یک‌بار ساخته می‌شود و تا unmount همان می‌ماند.
  const stable = React.useRef<T | null>(null);
  if (stable.current === null) {
    stable.current = ((...args: Parameters<T>) =>
      (ref.current as (...a: Parameters<T>) => ReturnType<T>)(...args)) as unknown as T;
  }
  return stable.current;
};
